import { useState, useRef } from 'react'
import { Card, CardHeader, CardTitle, CardDescription, CardContent, Button, Input, Label, Select } from '@/components/ui'
import { GraduationCap, Download, RefreshCw, Printer } from 'lucide-react'
import { toast } from 'sonner'
import jsPDF from 'jspdf'
import html2canvas from 'html2canvas'

interface StudentData {
  name: string
  studentId: string
  program: string
  yearLevel: string
  semester: string
  academicYear: string
  tuitionFee: number
  miscFee: number
  labFee: number
  libraryFee: number
  athleticFee: number
  totalDue: number
  dueDate: string
  paymentStatus: string
}

const programs = [
  'BS Computer Science',
  'BS Information Technology',
  'BS Computer Engineering',
  'BS Business Administration',
  'BS Accountancy',
  'BS Nursing',
  'BS Psychology',
  'BS Education',
]

const yearLevels = ['1st Year', '2nd Year', '3rd Year', '4th Year', '5th Year']
const semesters = ['1st Semester', '2nd Semester', 'Summer']

export function StudentSlip() {
  const slipRef = useRef<HTMLDivElement>(null)
  const [data, setData] = useState<StudentData>({
    name: 'Juan Dela Cruz',
    studentId: '2024-00001',
    program: 'BS Computer Science',
    yearLevel: '2nd Year',
    semester: '1st Semester',
    academicYear: '2025-2026',
    tuitionFee: 35000,
    miscFee: 5000,
    labFee: 3000,
    libraryFee: 1500,
    athleticFee: 500,
    totalDue: 45000,
    dueDate: '2025-09-15',
    paymentStatus: 'Unpaid',
  })

  const updateField = (field: keyof StudentData, value: string | number) => {
    setData((prev) => {
      const updated = { ...prev, [field]: value }
      // Recalculate total
      if (['tuitionFee', 'miscFee', 'labFee', 'libraryFee', 'athleticFee'].includes(field)) {
        updated.totalDue =
          Number(updated.tuitionFee) +
          Number(updated.miscFee) +
          Number(updated.labFee) +
          Number(updated.libraryFee) +
          Number(updated.athleticFee)
      }
      return updated
    })
  }

  const randomize = () => {
    const firstNames = ['Juan', 'Maria', 'Jose', 'Ana', 'Pedro', 'Elena', 'Carlos', 'Sofia']
    const lastNames = ['Dela Cruz', 'Santos', 'Reyes', 'Garcia', 'Mendoza', 'Lopez', 'Martinez', 'Gonzales']
    const name = `${firstNames[Math.floor(Math.random() * firstNames.length)]} ${lastNames[Math.floor(Math.random() * lastNames.length)]}`
    const studentId = `2024-${String(Math.floor(Math.random() * 99999)).padStart(5, '0')}`
    const tuition = Math.floor(Math.random() * 30000) + 20000
    const misc = Math.floor(Math.random() * 5000) + 3000
    const lab = Math.floor(Math.random() * 3000) + 1000
    const library = Math.floor(Math.random() * 2000) + 500
    const athletic = Math.floor(Math.random() * 1000) + 200

    setData({
      name,
      studentId,
      program: programs[Math.floor(Math.random() * programs.length)],
      yearLevel: yearLevels[Math.floor(Math.random() * yearLevels.length)],
      semester: semesters[Math.floor(Math.random() * semesters.length)],
      academicYear: '2025-2026',
      tuitionFee: tuition,
      miscFee: misc,
      labFee: lab,
      libraryFee: library,
      athleticFee: athletic,
      totalDue: tuition + misc + lab + library + athletic,
      dueDate: '2025-09-15',
      paymentStatus: Math.random() > 0.5 ? 'Unpaid' : 'Paid',
    })
    toast.success('Data randomized!')
  }

  const downloadPDF = async () => {
    if (!slipRef.current) return

    try {
      const canvas = await html2canvas(slipRef.current, {
        scale: 2,
        backgroundColor: '#ffffff',
      })

      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
      })

      const imgWidth = 190
      const imgHeight = (canvas.height * imgWidth) / canvas.width

      pdf.addImage(canvas.toDataURL('image/png'), 'PNG', 10, 10, imgWidth, imgHeight)
      pdf.save(`student_slip_${data.studentId}.pdf`)
      toast.success('PDF downloaded!')
    } catch {
      toast.error('Failed to generate PDF')
    }
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-PH', {
      style: 'currency',
      currency: 'PHP',
    }).format(amount)
  }

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <div className="flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10">
            <GraduationCap className="h-6 w-6 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Student Billing Slip</h1>
            <p className="text-muted-foreground">Generate student billing and assessment slips</p>
          </div>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-[400px_1fr]">
        {/* Form */}
        <Card className="glass h-fit">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Student Information</CardTitle>
              <Button variant="outline" size="sm" onClick={randomize}>
                <RefreshCw className="h-4 w-4 mr-1" />
                Randomize
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2 space-y-1">
                <Label className="text-xs">Student Name</Label>
                <Input
                  value={data.name}
                  onChange={(e) => updateField('name', e.target.value)}
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Student ID</Label>
                <Input
                  value={data.studentId}
                  onChange={(e) => updateField('studentId', e.target.value)}
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Academic Year</Label>
                <Input
                  value={data.academicYear}
                  onChange={(e) => updateField('academicYear', e.target.value)}
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Program</Label>
                <Select value={data.program} onChange={(e) => updateField('program', e.target.value)}>
                  {programs.map((p) => (
                    <option key={p} value={p}>{p}</option>
                  ))}
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Year Level</Label>
                <Select value={data.yearLevel} onChange={(e) => updateField('yearLevel', e.target.value)}>
                  {yearLevels.map((y) => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Semester</Label>
                <Select value={data.semester} onChange={(e) => updateField('semester', e.target.value)}>
                  {semesters.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Due Date</Label>
                <Input
                  type="date"
                  value={data.dueDate}
                  onChange={(e) => updateField('dueDate', e.target.value)}
                />
              </div>
            </div>

            <div className="pt-4 border-t border-border">
              <Label className="text-xs text-muted-foreground mb-3 block">Fee Breakdown</Label>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Tuition Fee</Label>
                  <Input
                    type="number"
                    value={data.tuitionFee}
                    onChange={(e) => updateField('tuitionFee', Number(e.target.value))}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Misc Fee</Label>
                  <Input
                    type="number"
                    value={data.miscFee}
                    onChange={(e) => updateField('miscFee', Number(e.target.value))}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Lab Fee</Label>
                  <Input
                    type="number"
                    value={data.labFee}
                    onChange={(e) => updateField('labFee', Number(e.target.value))}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Library Fee</Label>
                  <Input
                    type="number"
                    value={data.libraryFee}
                    onChange={(e) => updateField('libraryFee', Number(e.target.value))}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Athletic Fee</Label>
                  <Input
                    type="number"
                    value={data.athleticFee}
                    onChange={(e) => updateField('athleticFee', Number(e.target.value))}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Status</Label>
                  <Select value={data.paymentStatus} onChange={(e) => updateField('paymentStatus', e.target.value)}>
                    <option value="Unpaid">Unpaid</option>
                    <option value="Paid">Paid</option>
                    <option value="Partial">Partial</option>
                  </Select>
                </div>
              </div>
            </div>

            <div className="flex gap-2 pt-4">
              <Button onClick={downloadPDF} className="flex-1">
                <Download className="h-4 w-4 mr-2" />
                Download PDF
              </Button>
              <Button variant="outline" onClick={() => window.print()}>
                <Printer className="h-4 w-4" />
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Preview */}
        <Card className="glass overflow-hidden">
          <CardHeader>
            <CardTitle>Preview</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex justify-center bg-muted/20 p-4 rounded-lg">
              <div
                ref={slipRef}
                className="bg-white text-black p-8 w-full max-w-[600px] shadow-lg"
                style={{ fontFamily: 'Arial, sans-serif' }}
              >
                {/* Header */}
                <div className="text-center border-b-2 border-black pb-4 mb-4">
                  <h1 className="text-xl font-bold">UNIVERSITY OF TECHNOLOGY</h1>
                  <p className="text-sm">123 Academic Avenue, Metro Manila, Philippines</p>
                  <p className="text-xs text-gray-600">Tel: (02) 1234-5678 | Email: registrar@utech.edu.ph</p>
                </div>

                {/* Title */}
                <div className="text-center mb-6">
                  <h2 className="text-lg font-bold uppercase tracking-wide">Student Assessment Slip</h2>
                  <p className="text-sm">{data.semester} - A.Y. {data.academicYear}</p>
                </div>

                {/* Student Info */}
                <div className="grid grid-cols-2 gap-2 text-sm mb-6 bg-gray-50 p-4 rounded">
                  <div><span className="font-semibold">Name:</span> {data.name}</div>
                  <div><span className="font-semibold">Student ID:</span> {data.studentId}</div>
                  <div><span className="font-semibold">Program:</span> {data.program}</div>
                  <div><span className="font-semibold">Year Level:</span> {data.yearLevel}</div>
                </div>

                {/* Fee Table */}
                <table className="w-full text-sm mb-6">
                  <thead>
                    <tr className="border-b-2 border-black">
                      <th className="text-left py-2">Description</th>
                      <th className="text-right py-2">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b">
                      <td className="py-2">Tuition Fee</td>
                      <td className="text-right">{formatCurrency(data.tuitionFee)}</td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-2">Miscellaneous Fee</td>
                      <td className="text-right">{formatCurrency(data.miscFee)}</td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-2">Laboratory Fee</td>
                      <td className="text-right">{formatCurrency(data.labFee)}</td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-2">Library Fee</td>
                      <td className="text-right">{formatCurrency(data.libraryFee)}</td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-2">Athletic Fee</td>
                      <td className="text-right">{formatCurrency(data.athleticFee)}</td>
                    </tr>
                    <tr className="border-t-2 border-black font-bold">
                      <td className="py-2">TOTAL AMOUNT DUE</td>
                      <td className="text-right text-lg">{formatCurrency(data.totalDue)}</td>
                    </tr>
                  </tbody>
                </table>

                {/* Payment Info */}
                <div className="flex justify-between text-sm mb-6 p-3 bg-gray-100 rounded">
                  <div>
                    <span className="font-semibold">Due Date:</span>{' '}
                    {new Date(data.dueDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                  </div>
                  <div>
                    <span className="font-semibold">Status:</span>{' '}
                    <span className={data.paymentStatus === 'Paid' ? 'text-green-600' : 'text-red-600'}>
                      {data.paymentStatus}
                    </span>
                  </div>
                </div>

                {/* Footer */}
                <div className="text-center text-xs text-gray-500 border-t pt-4">
                  <p>This is a computer-generated document. No signature required.</p>
                  <p>Generated on: {new Date().toLocaleDateString()}</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
